/**
 * File utilities for upload handling.
 *
 * Cloud storage (S3) keys are derived from file names on the backend, so names
 * with accents, spaces, parentheses, or repeated dots (e.g. screenshots named
 * "Captura de pantalla 2026-06-23 a la(s) 2.45.22 p. m..png") can fail with
 * "Failed to upload file to cloud storage". Sanitize names client-side before
 * submitting files.
 */

const MAX_BASE_NAME_LENGTH = 100;

/**
 * Sanitize a file name so it is safe to use as a cloud storage key.
 * - Strips diacritics (á -> a) via unicode normalization
 * - Replaces any character outside [A-Za-z0-9._-] with an underscore
 * - Collapses repeated underscores/dots and trims leading/trailing separators
 * - Preserves the file extension and caps the base name length
 */
export function sanitizeFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  const base = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
  const ext = lastDot > 0 ? fileName.slice(lastDot + 1) : '';

  const clean = (s: string) =>
    s
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/\.{2,}/g, '.')
      .replace(/^[_.-]+|[_.-]+$/g, '');

  const safeBase = (clean(base) || 'file').slice(0, MAX_BASE_NAME_LENGTH);
  const safeExt = clean(ext);
  return safeExt ? `${safeBase}.${safeExt}` : safeBase;
}

/**
 * Return a File whose name is safe for cloud storage upload.
 * Returns the original File unchanged when its name is already safe.
 */
export function sanitizeFileForUpload(file: File): File {
  const safeName = sanitizeFileName(file.name);
  if (safeName === file.name) return file;
  return new File([file], safeName, { type: file.type, lastModified: file.lastModified });
}

/** Sanitize a batch of files for upload. */
export function sanitizeFilesForUpload(files: File[] | FileList): File[] {
  return Array.from(files).map(sanitizeFileForUpload);
}
